import type { Component } from 'solid-js';
import { createSignal, Match, onMount, Show, Switch } from 'solid-js'
import Login from '@pages/Login';
import Home from '@pages/Home';
import Nav from '@components/Nav'
import { getClerk } from '@utils/clerk'
import Bookkeeper from '@pages/Bookkeeper';

const App: Component = () => {
  const [authenticated, setAuthenticated] = createSignal<boolean | null>(null)
  const [authorized, setAuthorized] = createSignal<boolean | null>(null)
  const [activeTab, setActiveTab] = createSignal<string>('home')


  onMount(async () => {
    try {
      const clerk = await getClerk()
      if (typeof clerk.load === 'function') await clerk.load()

      // Handle any Clerk redirect callbacks but keep routing local.
      if (typeof clerk.handleRedirectCallback === 'function') {
        try {
          await clerk.handleRedirectCallback({}, () => { })
        } catch (_) {
          // ignore
        }
      }

      // Clean Clerk-related query params from URL
      try {
        const url = new URL(window.location.href)
        const params = url.searchParams
        const clerkParams = ['__clerk_status', '__clerk_created_session', '__clerk_ticket']
        let removed = false
        clerkParams.forEach((p) => {
          if (params.has(p)) {
            params.delete(p)
            removed = true
          }
        })
        if (removed) {
          window.history.replaceState({}, '', `${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash}`)
        }
      } catch (_) { }

      const isSignedIn = Boolean((clerk as any)?.isSignedIn)
      setAuthenticated(isSignedIn)

      // If signed in, try to determine admin status from the user's public section.
      if (isSignedIn) {
        try {
          // Try common locations for Clerk user public metadata.
          const user = (clerk as any)?.user || (clerk as any)?.client?.user || null

          const publicMeta = user?.publicMetadata ?? user?.public_metadata ?? null

          // Accept either boolean or truthy string values.
          const adminFlag = publicMeta && (publicMeta.isAdmin ?? publicMeta.admin ?? publicMeta.is_admin)

          if (typeof adminFlag === 'boolean') {
            setAuthorized(Boolean(adminFlag))
          } else if (typeof adminFlag === 'string') {
            setAuthorized(adminFlag === 'true' || adminFlag === '1')
          } else {
            // As a conservative fallback, consider missing admin claim as not authorized.
            setAuthorized(false)
          }
        } catch (err) {
          setAuthorized(false)
        }
      } else {
        setAuthorized(null)
      }
    } catch (err) {
      setAuthenticated(false)
    }
  })

  return (
    <div class="min-h-screen bg-gray-900 text-white flex flex-col items-center">
      <Nav onClickLink={setActiveTab} />
      <Show
        when={authenticated() === null}
        fallback={
          authenticated() === true ? (
            // Signed in — render based on authorization
            authorized() === null ? (
              <main class="flex-1 flex items-center justify-center w-full">
                <p class="text-white">Checking authorization…</p>
              </main>
            ) : authorized() === true ? (
              <Switch>
                <Match when={activeTab() === 'home'}>
                  <Home />
                </Match>
                <Match when={activeTab() === 'bookkeeper'}>
                  <Bookkeeper />
                </Match>
              </Switch>
            ) : (
              <main class="flex-1 flex items-center justify-center w-full">
                <div class="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-lg p-6 text-center">
                  <h2 class="text-2xl font-semibold text-white mb-4">Unauthorized</h2>
                  <p class="text-gray-300">Your account is not authorized to access this area.</p>
                </div>
              </main>
            )
          ) : (
            <Login />
          )
        }
      >
        <main class="flex-1 flex items-center justify-center w-full">
          <p class="text-white">Checking authentication…</p>
        </main>
      </Show>
    </div>
  )
}

export default App;
